import * as openpgp from "openpgp";

// JEDYNE miejsce, które czyta pakiet klucza PGP z tego, co przyszło z serwera albo z
// localStorage. To jest inwariant I3 z docs/model-kluczy-firmowych.md (sekcja 2):
//
//   „Jeden format: klucz publiczny = base64 binarnego packetlistu; wiadomość = base64 binarne.
//    Format czyta DOKŁADNIE JEDNA funkcja, która RZUCA przy keys.length === 0."
//
// POWÓD (ryzyko 2 dokumentu). openpgp 4.10 przy błędzie NIE RZUCA — `key.read` i
// `key.readArmored` zwracają `{ keys: [], err: [...] }`. Kilkanaście miejsc w kodzie robi
// `(await openpgp.key.readArmored(x)).keys[0]` i wpuszcza `undefined` dalej; istniejący guard
// „if (!encryptKey) throw" tego NIE łapie, bo sam obiekt wyniku jest prawdziwy. Po podmianie
// klucza profilu (armored) na klucz firmy (binarny packetlist) te miejsca zamilkłyby zamiast
// się wywalić — a objawem byłaby „pusta treść u części użytkowników", nie awaria.
//
// Dlatego ta funkcja przyjmuje OBA formaty (binarny packetlist w base64 ORAZ ASCII-armor,
// także zapakowany w base64 — tak wygląda dziś `intermidatePrivateKey.data`) i RZUCA, gdy
// nie wyszedł z tego ani jeden klucz.

/** Błąd formatu/materiału klucza. Jawny, żeby nie ginął w cichym `keys[0] === undefined`. */
export class PublicKeyReadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PublicKeyReadError";
  }
}

const ARMOR_MARKER = "-----BEGIN PGP";

const toBytes = (binary: string): Uint8Array => {
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

const unwrap = (result: openpgp.key.KeyResult): openpgp.key.Key => {
  const key = result?.keys?.[0];
  if (!key) {
    const details = (result?.err ?? [])
      .map((e) => (e instanceof Error ? e.message : String(e)))
      .join("; ");
    throw new PublicKeyReadError(
      details
        ? `Nie udało się odczytać klucza PGP: ${details}`
        : "Nie udało się odczytać klucza PGP (zero kluczy w pakiecie)",
    );
  }
  return key;
};

/**
 * Wspólny parser pakietu klucza — publicznego LUB tajnego (openpgp czyta jedne i drugie tym
 * samym `key.read`/`key.readArmored`). Używają go readPublicKey oraz utils/guestKeys.
 * RZUCA `PublicKeyReadError`, gdy z wejścia nie da się odczytać ani jednego klucza.
 */
export const readKeyMaterial = async (
  raw: string | Uint8Array | ArrayBuffer | null | undefined,
): Promise<openpgp.key.Key> => {
  if (raw === null || raw === undefined) {
    throw new PublicKeyReadError("Brak materiału klucza (null/undefined)");
  }

  if (typeof raw !== "string") {
    const bytes = raw instanceof Uint8Array ? raw : new Uint8Array(raw);
    if (!bytes.length) throw new PublicKeyReadError("Pusty pakiet klucza");
    return unwrap(await openpgp.key.read(bytes));
  }

  const text = raw.trim();
  if (!text.length) throw new PublicKeyReadError("Pusty materiał klucza");

  // 1. ASCII-armor wprost.
  if (text.includes(ARMOR_MARKER)) {
    return unwrap(await openpgp.key.readArmored(text));
  }

  // 2. base64 — albo binarnego packetlistu (format docelowy), albo armoru (format zastany,
  //    m.in. `myKeys.intermidatePrivateKey.data`, gdzie w kodzie stoi dziś `atob(...)`).
  let binary: string;
  try {
    binary = atob(text.replace(/\s+/g, ""));
  } catch {
    throw new PublicKeyReadError(
      "Materiał klucza nie jest ani ASCII-armor, ani base64",
    );
  }

  if (binary.trimStart().startsWith(ARMOR_MARKER)) {
    return unwrap(await openpgp.key.readArmored(binary));
  }

  return unwrap(await openpgp.key.read(toBytes(binary)));
};

/**
 * Klucz PUBLICZNY odbiorcy, w obu formatach (base64 binarnego packetlistu ORAZ ASCII-armor).
 * RZUCA `PublicKeyReadError` zamiast oddawać `undefined` — to jest cała różnica wobec
 * dzisiejszych ~jedenastu wywołań `openpgp.key.readArmored(...)` rozsianych po komponentach.
 */
export const readPublicKey = readKeyMaterial;

/** Nazwa używana w docs/model-kluczy-firmowych.md (inwariant I3). Ten sam parser. */
export const readPublicKeyBase64 = readKeyMaterial;
